import React from 'react';
import { withRouter } from 'react-router';
import { StackView, Box, Link, Heading, remRhythm, greaterThan } from 'localmed-core';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { Home, Phone } from 'react-feather';
import tinycolor from 'tinycolor2';

import Breadcrumb from '../../../../../shared/components/Breadcrumb';

import { buildLocationUrl } from '../../../../../shared/utils/urls';

import ProfileMap from './ProfileMap';

const ProfileContent = styled(Box)(
  ({ theme }) => css`
    background: linear-gradient(
      to bottom,
      ${tinycolor(theme.colors.legacyBackground).setAlpha(0).toString()} ${remRhythm(2)},
      ${theme.colors.legacyBackground} ${remRhythm(4)}
    );
    margin-left: auto;
    margin-right: auto;
    margin-top: ${remRhythm(-5)};
    position: relative;
    z-index: 1;
  `,
  greaterThan(
    'sm',
    ({ theme }) => css`
      background: linear-gradient(
        to bottom,
        ${tinycolor(theme.colors.legacyBackground).setAlpha(0).toString()} 0%,
        ${theme.colors.legacyBackground} ${remRhythm(2)}
      );
      margin-top: ${remRhythm(-3)};
    `
  )
);

const ProfileWrapper = styled(StackView)(
  ({ theme }) => css`
    max-width: ${theme.breakpoints.xl};
    margin: 0 auto;
  `
);

const ImageWrapper = styled(Box)(
  ({ theme }) => css`
    display: inline-block;
    flex-shrink: 0;
    border-radius: ${theme.radii.sm};
    box-shadow: ${theme.elevations[1]};
    border: 1px solid ${theme.colors.grays[100]};
    background-color: ${theme.colors.white};

    img {
      display: block;
      max-width: 100%;
      height: auto;
    }
  `
);

const ProfileInfoTitle = styled('dt')(
  ({ theme }) =>
    css`
      float: left;
      clear: both;
      line-height: ${theme.lineHeights.heading};
    `
);

const ProfileInfoDescription = styled('dd')(
  ({ theme }) =>
    css`
      line-height: ${theme.lineHeights.heading};
      margin-bottom: ${theme.space.xs};
    `
);

function Profile({ provider, history: { location } }) {
  const { display, practice, allProvidersForProfile, office, profile } = provider;
  const offices = allProvidersForProfile.nodes.map((value) => value.office);
  const { countryDetails, stateDetails, cityDetails } = office;

  const displayBreadcrumb = countryDetails && stateDetails && cityDetails;
  return (
    <Box position="relative">
      <ProfileMap offices={offices} activeOffice={provider.office} height={remRhythm(6)} />
      <ProfileContent paddingX={{ _: 'sm', lg: 'md' }}>
        <ProfileWrapper
          spacing={{ _: 'xs', sm: 'sm' }}
          direction={{ _: 'column', sm: 'row' }}
          alignItems="flex-end"
        >
          <ImageWrapper padding={{ _: 'xs', md: 'sm' }}>
            <Box as="img" src={profile.photo} alt="" width={[58, 94, 130]} />
          </ImageWrapper>
          <StackView spacing="xxs">
            {displayBreadcrumb && (
              <Breadcrumb aria-label="Breadcrumb" fontSize={{ _: 'xxs', md: 'xs' }}>
                <Link color="inherit" href="/" itemProp="item">
                  <span itemProp="name">Home</span>
                  <meta itemProp="position" content="1" />
                </Link>
                <Link
                  itemProp="item"
                  color="inherit"
                  href={buildLocationUrl(
                    { countrySlug: countryDetails.slug, stateSlug: stateDetails.slug },
                    { location }
                  )}
                >
                  <span itemProp="name">{stateDetails.name}</span>
                  <meta itemProp="position" content="2" />
                </Link>
                <Link
                  itemProp="item"
                  color="inherit"
                  href={buildLocationUrl(
                    {
                      countrySlug: countryDetails.slug,
                      stateSlug: stateDetails.slug,
                      citySlug: cityDetails.slug,
                    },
                    { location }
                  )}
                >
                  <span itemProp="name">{cityDetails.name}</span>
                  <meta itemProp="position" content="3" />
                </Link>
              </Breadcrumb>
            )}
            <Heading
              tier="h1"
              fontWeight="bold"
              fontSize={{ _: 'lg', md: 'xl' }}
              data-qa="profile__name"
            >
              {display}
            </Heading>
            <Box as="dl" fontSize={{ _: 'sm', md: 'md' }}>
              <ProfileInfoTitle>
                <span title="Practice">
                  <Home size="0.8em" />
                </span>
              </ProfileInfoTitle>
              <ProfileInfoDescription>{practice.display}</ProfileInfoDescription>
              <ProfileInfoTitle>
                <span title="Phone Number">
                  <Phone size="0.8em" />
                </span>
              </ProfileInfoTitle>
              <ProfileInfoDescription>{office.phone}</ProfileInfoDescription>
            </Box>
          </StackView>
        </ProfileWrapper>
      </ProfileContent>
    </Box>
  );
}

export default withRouter(Profile);
